// Consignment and outsourced production rules (ERP-PLAN §5.7), shared by the server (mutations, MCP) and the
// Consignment page / PO panel, so both agree on what a make order needs, what's reserved, what's short, when a
// count is due and what the stock at a CM is worth.
//
// - A make order keeps a snapshot of one unit's components (`makeComponents()`, from a BOM revision); the PO
//   line's qty is how many. Components HHL consigns (not services, not what the CM buys) come out of the CM's
//   stock: per_unit (BOM qty with scrap allowance) × output, consumed when output is reported.
// - Reservations aren't stored: `summarizeCms()` gives each open make order, in priority order (sent before draft,
//   then ships-by date, then PO number), what's left of the CM's stock for what it still has to build.
// - Stock at a CM: local items from the ledger (stock_balance), Shopify items from Shopify (stock_level, at a CM site
//   that is a Shopify location). Shipments post when shipped, so "in transit" is part of the CM's balance until the
//   CM confirms it.
import { type BomIndex, type BomItem, type BomLineRow, type BomRow, bomOf, byCm, type Resolved, resolve, type Supply } from "./boms.ts";
import type { LineCharge } from "./charges.ts";
import type {
  CmCount,
  CmCountLine,
  CmReport,
  CmReportLine,
  ConsignShipment,
  ConsignShipmentLine,
  Item,
  Location,
  MakeOrder,
  Po,
  PoLine,
  Receipt,
  StockMove,
} from "./db.gen.ts";
import { groupBy } from "./group.ts";
import { mulRound } from "./po.ts";

export const MAKE_STAGES = ["planned", "materials_ready", "smt", "test", "assembly", "packing", "shipped"] as const;
export type MakeStage = (typeof MAKE_STAGES)[number];
export const MAKE_STAGE_LABELS: Record<MakeStage, string> = {
  planned: "Planned",
  materials_ready: "Materials ready",
  smt: "SMT",
  test: "Test",
  assembly: "Assembly",
  packing: "Packing",
  shipped: "Shipped",
};

export const SCRAP_REASONS = ["defective", "damaged", "lost", "setup", "other"] as const;
export type ScrapReason = (typeof SCRAP_REASONS)[number];
export const SCRAP_REASON_LABELS: Record<ScrapReason, string> = {
  defective: "Defective",
  damaged: "Damaged",
  lost: "Lost",
  setup: "Setup / line loss",
  other: "Other",
};

export const COUNT_POLICIES = ["monthly", "per_build", "none"] as const;
export type CountPolicy = (typeof COUNT_POLICIES)[number];
export const COUNT_POLICY_LABELS: Record<CountPolicy, string> = { monthly: "Every month", per_build: "After each build", none: "Not prompted" };
/** A monthly count is due this many days after the last one. */
export const COUNT_EVERY_DAYS = 30;

const EPS = 1e-9;
/** Quantities to 3 decimals, rounded like Postgres. */
export const q3 = (n: number) => mulRound(n, 1, 3);
const q6 = (n: number) => mulRound(n, 1, 6);

// Snapshot ---------------------------------------------------------------------------------------------

/** One unit's need of one part, as a make order keeps it. */
export type MakeComponent = {
  item_id: string;
  sku: string;
  name: string;
  kind: string;
  source: string;
  supply: Supply;
  /** BOM quantity with scrap allowance, through phantom sub-assemblies. */
  per_unit: number;
  /** BOM quantity without scrap. */
  base_per_unit: number;
  unit_cost: number | null;
};

/** Comes out of the CM's stock: HHL consigns it, and it isn't a service. */
export const isConsigned = (c: Pick<MakeComponent, "supply" | "kind">) => c.supply === "hhl_consigns" && c.kind !== "service";

export const componentsOf = (mo: Pick<MakeOrder, "components">): MakeComponent[] => (Array.isArray(mo.components) ? (mo.components as MakeComponent[]) : []);

export type MakeSnapshot = {
  components: MakeComponent[];
  /**
   * Charges --- a make PO line's first charges when the BOM has charges (its own and its phantoms'), with a row for
   * the parts (and packaging) the CM buys and one per service, so they add up to what the CM is paid; null without.
   */
  lineCharges: LineCharge[] | null;
  /** HHL's consigned parts per unit (USD); null when one of them has no cost. */
  material: number | null;
  /** What the CM charges per unit by the BOM: services plus the parts it buys (a default for the PO line's price). */
  cmCost: number;
  warnings: string[];
};

/**
 * One unit of `variant` (the item being built) by a BOM revision, flattened per part and supply. With `qty` and
 * `stockOf` (a make order being made): a sub-assembly built in the same order comes from its own stock first (at HHL
 * and the CM: shared/boms.ts builtInside()), as a share of each unit (300 in stock for 1,000 builds: 0.3 of one, and
 * 0.7 of its parts), so what the CM uses as it reports output adds up to the stock, then the parts.
 */
export function makeComponents(
  ix: BomIndex,
  bom: BomRow,
  variant: BomItem | undefined,
  opts: {
    qty?: number;
    stockOf?: (itemId: string) => number;
    /** Production runs --- who supplies a part for the run this build is on, where it says (migration 057). */
    supplyOf?: (itemId: string) => Supply | undefined;
  } = {},
): MakeSnapshot {
  const resolved = resolve(ix, bom, variant);
  // The run's word on a part's supply replaces the BOM line's (never a sub-assembly built inside: that's how it's made).
  const r: Resolved = opts.supplyOf
    ? {
        ...resolved,
        lines: resolved.lines.map((l) => {
          const s = l.phantom ? undefined : opts.supplyOf?.(l.itemId);
          return s && s !== l.supply ? { ...l, supply: s } : l;
        }),
      }
    : resolved;
  const lineById = new Map<string, BomLineRow>();
  for (const list of ix.linesByBom.values()) for (const l of list) lineById.set(l.id, l);
  // The share of each sub-assembly built in the same order that its stock covers, by its heading's key.
  const fromStock = new Map<string, number>();
  const fromStockByItem = new Map<string, number>();
  if (opts.qty && opts.stockOf)
    for (const h of r.lines)
      if (h.phantom && h.depth === 0 && h.item.source === "local" && h.supply === "hhl_consigns" && h.qty > 0) {
        const f = Math.min(1, Math.max(0, opts.stockOf(h.itemId)) / (opts.qty * h.qty));
        if (f > 0) {
          fromStock.set(h.key, f);
          fromStockByItem.set(h.itemId, f);
        }
      }
  /** How much of a line (or of a charge, by the sub-assembly it's on) is still built here (1 unless partly in stock). */
  const left: Left = {
    line: (key) => 1 - (fromStock.get(key.split("/")[0]) ?? 0),
    charge: (c) => (c.via.length ? 1 - (fromStockByItem.get(c.via[0]) ?? 0) : 1),
  };
  const out = new Map<string, MakeComponent>();
  const add = (l: (typeof r.lines)[number], qty: number, factor: number) => {
    const key = `${l.itemId}|${l.supply}`;
    const row = out.get(key);
    if (row) {
      row.per_unit += qty;
      row.base_per_unit += qty / factor;
    } else {
      out.set(key, {
        item_id: l.itemId,
        sku: l.item.sku,
        name: l.item.name,
        kind: l.item.kind,
        source: l.item.source,
        supply: l.supply,
        per_unit: qty,
        base_per_unit: qty / factor,
        unit_cost: l.unitCost,
      });
    }
  };
  for (const l of r.lines) {
    // The resolved qty has every scrap % along its path (phantom parents too) multiplied in.
    const factor = l.key.split("/").reduce((f, id) => f * (1 + Number(lineById.get(id)?.scrap_pct ?? 0) / 100), 1);
    if (l.phantom) {
      const f = fromStock.get(l.key);
      if (f) add(l, l.qty * f, factor); // the share that comes from its stock, as itself
      continue;
    }
    const share = left.line(l.key);
    if (share > 1e-9) add(l, l.qty * share, factor);
  }
  const components = [...out.values()]
    .map((c) => ({ ...c, per_unit: q6(c.per_unit), base_per_unit: q6(c.base_per_unit) }))
    .sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true, sensitivity: "base" }));
  const consigned = components.filter(isConsigned);
  const material = consigned.some((c) => c.unit_cost == null) ? null : consigned.reduce((n, c) => n + c.per_unit * Number(c.unit_cost), 0);
  return {
    components,
    material: material == null ? null : mulRound(material, 1, 4),
    // Charges --- the maker's per-unit charges are in what the CM is paid (per-order ones: lineCharges, by quantity); a
    // sub-assembly taken from stock needs none of its own work or parts, so only its share built here counts.
    cmCost: mulRound(cmCostOf(r, left), 1, 4),
    lineCharges: firstCharges(ix, r, left),
    warnings: r.warnings,
  };
}

type Left = { line: (key: string) => number; charge: (c: Resolved["charges"][number]) => number };
const ALL_HERE: Left = { line: () => 1, charge: () => 1 };

/** What the CM charges per unit by the BOM: the parts it buys, services and per-unit charges, each at its share built here. */
function cmCostOf(r: Resolved, left: Left): number {
  let n = 0;
  for (const l of r.lines) if (!l.phantom && l.extCost != null && (l.item.kind === "service" || byCm(l.supply))) n += l.extCost * left.line(l.key);
  for (const c of r.charges) if (c.per === "unit" && c.amount != null) n += c.amount * c.qty * left.charge(c);
  return n;
}

/** Charges --- a make PO line's first charges from a resolved BOM with charges (see MakeSnapshot), else null. */
function firstCharges(ix: BomIndex, r: Resolved, left: Left = ALL_HERE): LineCharge[] | null {
  if (!r.charges.length) return null;
  const q4 = (n: number) => mulRound(n, 1, 4);
  const rows: LineCharge[] = r.charges.flatMap((c) => {
    const under = c.via.length ? ix.itemById.get(c.via[c.via.length - 1])?.sku : "";
    const share = c.per === "order" ? (left.charge(c) > 1e-9 ? 1 : 0) : left.charge(c);
    if (share <= 1e-9) return []; // all of it from stock: none of its work in this order
    return [
      {
        name: under ? `${c.name} (${under})` : c.name,
        amount: q4(c.per === "order" ? (c.amount ?? 0) : (c.amount ?? 0) * c.qty * share),
        per: c.per,
        layer: c.layer,
      },
    ];
  });
  let parts = 0;
  let packaging = 0;
  for (const l of r.lines) {
    if (l.phantom || l.extCost == null) continue;
    const ext = l.extCost * left.line(l.key);
    if (ext <= 1e-9) continue;
    if (l.item.kind === "service") rows.push({ name: l.item.name, amount: q4(ext), per: "unit", layer: "assembly" });
    else if (byCm(l.supply)) {
      if (l.packaging) packaging += ext;
      else parts += ext;
    }
  }
  if (parts > 0) rows.push({ name: "Parts the CM buys", amount: q4(parts), per: "unit", layer: "materials" });
  if (packaging > 0) rows.push({ name: "Packaging the CM buys", amount: q4(packaging), per: "unit", layer: "packaging" });
  return rows;
}

/** What one built unit is worth: HHL's parts plus the CM's fee (the PO line's price, in USD only). */
export function builtUnitCost(mo: Pick<MakeOrder, "material_cost">, line: Pick<PoLine, "unit_price"> | undefined, currency: string): number | null {
  if (mo.material_cost == null) return null;
  const fee = currency === "USD" && line?.unit_price != null ? Number(line.unit_price) : 0;
  return mulRound(Number(mo.material_cost) + fee, 1, 4);
}

/**
 * A unit cost for valuing stock when an item has no std_cost: made items cost their BOM roll-up first, bought
 * ones their preferred USD vendor price first (vendorCosts() in shared/forecast.ts), each falling back to the other.
 */
export function costLookup(vendorCost: ReadonlyMap<string, number>, ix?: BomIndex): (itemId: string) => number | null {
  const cache = new Map<string, number | null>();
  const rollup = (id: string) => {
    const item = ix?.itemById.get(id);
    const bom = ix && item ? bomOf(ix, item) : undefined;
    if (!ix || !bom) return null;
    const total = resolve(ix, bom, item).cost.total;
    return total > 0 ? total : null;
  };
  return (id) => {
    if (cache.has(id)) return cache.get(id) ?? null;
    const made = ix?.itemById.get(id)?.procurement === "make";
    const cost = made ? (rollup(id) ?? vendorCost.get(id) ?? null) : (vendorCost.get(id) ?? rollup(id));
    cache.set(id, cost);
    return cost;
  };
}

/** POs whose make orders still take from the CM's stock (drafts too, after the sent ones). */
const OPEN_PO = new Set(["draft", "sent", "acknowledged", "in_production", "shipped"]);

// Counts -------------------------------------------------------------------------------------------------

/** Whether a CM should be asked for a count, and why. */
export function countDue(
  policy: CountPolicy,
  lastCountedAt: string | null,
  /** Builds finished at this CM (all output reported), with when the last output came in. */
  finished: { label: string; at: string }[],
  hasStock: boolean,
  now: string,
): { due: boolean; reason: string } {
  if (policy === "none") return { due: false, reason: "" };
  if (policy === "per_build") {
    const since = finished.filter((f) => !lastCountedAt || f.at > lastCountedAt).sort((a, b) => b.at.localeCompare(a.at));
    if (since.length) return { due: true, reason: `${since[0].label} finished since the last count` };
    return { due: false, reason: "" };
  }
  if (!lastCountedAt) return hasStock ? { due: true, reason: "Never counted" } : { due: false, reason: "" };
  const days = Math.floor((Date.parse(now) - Date.parse(lastCountedAt)) / 86_400_000);
  return days >= COUNT_EVERY_DAYS ? { due: true, reason: `Last counted ${days} days ago` } : { due: false, reason: "" };
}

// Summary per CM -------------------------------------------------------------------------------------

export type CmItem = Pick<Item, "id" | "sku" | "name" | "uom" | "kind" | "source" | "std_cost" | "archived" | "photo_url">;
export type CmLocation = Pick<Location, "id" | "name" | "kind" | "vendor_id" | "is_cm" | "count_policy" | "shopify_location_id" | "active" | "ignored">;

/** The synced rows the summary reads (the browser passes its tables, the server queries the same columns). */
export type CmData = {
  locations: readonly CmLocation[];
  items: readonly CmItem[];
  balances: readonly { item_id: string; location_id: string; qty: number | string; last_counted_at: string | null }[];
  levels: readonly { item_id: string; location_id: string; on_hand: number | string }[];
  shipments: readonly ConsignShipment[];
  shipmentLines: readonly ConsignShipmentLine[];
  makeOrders: readonly MakeOrder[];
  pos: readonly Pick<Po, "id" | "number" | "status" | "kind" | "vendor_id" | "ship_to_location_id" | "expected_ship" | "expected_arrive" | "currency">[];
  /** Pre-sales --- qty_direct_shipped: a make PO line's units sent from the CM straight to backers. */
  poLines: readonly (Pick<PoLine, "id" | "po_id" | "item_id" | "sku" | "name" | "qty" | "qty_received" | "qty_rejected" | "unit_price" | "position"> &
    Partial<Pick<PoLine, "qty_direct" | "qty_direct_shipped">>)[];
  reports: readonly Pick<CmReport, "id" | "kind" | "location_id" | "make_order_id" | "qty_output" | "reported_at">[];
  reportLines: readonly Pick<CmReportLine, "report_id" | "item_id" | "kind" | "qty">[];
  counts: readonly Pick<CmCount, "id" | "number" | "location_id" | "status" | "counted_at">[];
  /** A unit cost for valuing stock, when an item has no std_cost (preferred vendor price, BOM roll-up). */
  costOf?: (itemId: string) => number | null;
  /** ISO time; decides whether a count is due. */
  now?: string;
  /** Pre-sales --- parts and add-ons set aside at a CM for backers' boxes (shared/presales.ts cmClaims()). */
  claims?: readonly ConsignClaim[];
};

/** Pre-sales --- units at a CM promised to backers' boxes: they come after the open make orders, and out of `free`. */
export type ConsignClaim = { location_id: string; item_id: string; qty: number; label: string };

export type ComponentNeed = MakeComponent & {
  consigned: boolean;
  /** BOM quantity × units ordered. */
  need: number;
  /** Scrap allowance × units ordered. */
  allowance: number;
  /** need + allowance: what the whole order takes. */
  total: number;
  consumed: number;
  /** What the units still to build take. */
  remaining: number;
  /** Set aside for this order from the CM's stock (in transit included). */
  reserved: number;
  /** remaining − reserved. */
  short: number;
  /** Short counting only what the CM has confirmed (not what's still in transit). */
  shortConfirmed: number;
};

export type MakeOrderState = {
  mo: MakeOrder;
  po: CmData["pos"][number];
  line: CmData["poLines"][number] | undefined;
  item: CmItem | undefined;
  qty: number;
  output: number;
  /** Delivered at the PO's ship-to (good and rejected). */
  delivered: number;
  /** Pre-sales --- sent from the CM straight to backers (shipments recorded on the run's pre-sale). */
  toBackers: number;
  /** Still to build. */
  remainingUnits: number;
  /** Built, still at the CM (for planning: "at CM, finished"): output − delivered − sent to backers. */
  atCm: number;
  /** Still takes from the CM's stock. */
  open: boolean;
  /**
   * ready: everything for the rest is at the CM; arriving: once what's in transit arrives; short: parts missing;
   * built: all output reported; closed: the PO is closed, cancelled or received.
   */
  status: "ready" | "arriving" | "short" | "built" | "closed";
  /** Units the CM can build now from what's set aside and confirmed there. */
  buildable: number;
  components: ComponentNeed[];
  builtCost: number | null;
};

export type CmStockLine = {
  item: CmItem | undefined;
  itemId: string;
  onHand: number;
  inTransit: number;
  reserved: number;
  /** Pre-sales --- set aside for backers' boxes after the open make orders (never more than what's left). */
  forBackers: number;
  free: number;
  /** A Shopify item: the number is Shopify's, and only Shopify changes it. */
  inShopify: boolean;
  lastCountedAt: string | null;
  unitCost: number | null;
  value: number | null;
};

export type ShipmentState = { shipment: ConsignShipment; lines: ConsignShipmentLine[]; units: number; value: number | null };

export type CmSummary = {
  location: CmLocation;
  stock: CmStockLine[];
  /** Every make order at this CM: open ones first, in priority order. */
  orders: MakeOrderState[];
  /** Shipped, not yet confirmed (both directions). */
  inTransit: ShipmentState[];
  /** POs from suppliers shipping straight to this CM, not all here yet. */
  inbound: { po: CmData["pos"][number]; lines: CmData["poLines"][number][]; due: number }[];
  counts: { pending: CmData["counts"][number][]; lastCountedAt: string | null; due: { due: boolean; reason: string } };
  value: {
    /** Everything HHL Ops counts as at the CM (ledger and Shopify stock, in transit to it included). */
    stock: number;
    /** Of which still in transit to the CM. */
    inTransit: number;
    /** Parts (and sub-assemblies kept for the next build): stock less the finished goods waiting to ship. */
    parts: number;
    /**
     * Built and waiting at the CM to ship (planning's "at CM, finished"): local items at their stock value (part of
     * `stock`), Shopify items, which have no ledger, at HHL's parts + the CM's fee (on top of `stock`).
     */
    finished: number;
    /** stock + Shopify items' finished goods: what's at risk at the CM. */
    total: number;
    /** Lines left out for want of a cost. */
    missing: number;
  };
};

const add = (m: Map<string, number>, k: string, n: number) => m.set(k, (m.get(k) ?? 0) + n);

/** Every CM site's stock, make orders (with reservations and readiness), shipments, counts and value. */
export function summarizeCms(d: CmData): Map<string, CmSummary> {
  const now = d.now ?? new Date().toISOString();
  const itemById = new Map(d.items.map((i) => [i.id, i]));
  const poById = new Map(d.pos.map((p) => [p.id, p]));
  const lineById = new Map(d.poLines.map((l) => [l.id, l]));
  const cms = d.locations.filter((l) => l.is_cm);
  const cmIds = new Set(cms.map((l) => l.id));
  const costOf = (id: string) => {
    const item = itemById.get(id);
    if (item?.std_cost != null) return Number(item.std_cost);
    return d.costOf?.(id) ?? null;
  };

  const balances = groupBy(
    d.balances.filter((b) => cmIds.has(b.location_id)),
    (b) => b.location_id,
  );
  const levels = groupBy(
    d.levels.filter((s) => cmIds.has(s.location_id)),
    (s) => s.location_id,
  );
  const linesByShipment = groupBy(d.shipmentLines, (l) => l.shipment_id);
  const shipmentsByCm = groupBy(
    d.shipments.filter((s) => cmIds.has(s.cm_location_id)),
    (s) => s.cm_location_id,
  );
  const ordersByCm = groupBy(
    d.makeOrders.filter((m) => cmIds.has(m.cm_location_id)),
    (m) => m.cm_location_id,
  );
  const reportsById = new Map(d.reports.map((r) => [r.id, r]));
  // Consumed so far per make order and part.
  const consumed = new Map<string, Map<string, number>>();
  for (const l of d.reportLines) {
    if (l.kind !== "consume") continue;
    const moId = reportsById.get(l.report_id)?.make_order_id;
    if (!moId) continue;
    let m = consumed.get(moId);
    if (!m) consumed.set(moId, (m = new Map()));
    add(m, l.item_id, Number(l.qty));
  }
  const lastOutput = new Map<string, string>(); // make order → when its last output came in
  for (const r of d.reports) {
    if (r.kind === "output" && r.make_order_id && (lastOutput.get(r.make_order_id) ?? "") < r.reported_at) lastOutput.set(r.make_order_id, r.reported_at);
  }
  const countsByCm = groupBy(
    d.counts.filter((c) => cmIds.has(c.location_id)),
    (c) => c.location_id,
  );
  const linesByPo = groupBy(d.poLines, (l) => l.po_id);

  const out = new Map<string, CmSummary>();
  for (const loc of cms) {
    // On hand at the CM: the ledger for local items, Shopify's numbers for Shopify items.
    const onHand = new Map<string, number>();
    const inShopify = new Set<string>();
    const counted = new Map<string, string | null>();
    for (const b of balances.get(loc.id) ?? []) {
      add(onHand, b.item_id, Number(b.qty));
      counted.set(b.item_id, b.last_counted_at);
    }
    for (const s of levels.get(loc.id) ?? []) {
      add(onHand, s.item_id, Number(s.on_hand));
      inShopify.add(s.item_id);
    }
    const inTransit: ShipmentState[] = [];
    const transitQty = new Map<string, number>();
    let transitValue = 0;
    for (const s of shipmentsByCm.get(loc.id) ?? []) {
      if (s.status !== "shipped") continue;
      const lines = (linesByShipment.get(s.id) ?? []).sort((a, b) => a.position - b.position);
      let value: number | null = 0;
      for (const l of lines) {
        const c = l.unit_cost != null ? Number(l.unit_cost) : costOf(l.item_id);
        value = value == null || c == null ? null : value + c * Number(l.qty);
        if (s.direction === "to_cm") {
          add(transitQty, l.item_id, Number(l.qty));
          if (c != null) transitValue += c * Number(l.qty);
        }
      }
      inTransit.push({ shipment: s, lines, units: lines.reduce((n, l) => n + Number(l.qty), 0), value });
    }
    inTransit.sort((a, b) => a.shipment.shipped_at.localeCompare(b.shipment.shipped_at));

    // Make orders: open ones take from the pools in priority order.
    const states: MakeOrderState[] = [];
    for (const mo of ordersByCm.get(loc.id) ?? []) {
      const po = poById.get(mo.po_id);
      if (!po) continue;
      const line = lineById.get(mo.po_line_id);
      const qty = Number(line?.qty ?? 0);
      const output = Number(mo.qty_output);
      const delivered = Number(line?.qty_received ?? 0) + Number(line?.qty_rejected ?? 0);
      const toBackers = Number(line?.qty_direct_shipped ?? 0);
      const remainingUnits = Math.max(0, q3(qty - output));
      const open = OPEN_PO.has(po.status) && remainingUnits > EPS;
      states.push({
        mo,
        po,
        line,
        item: itemById.get(mo.item_id),
        qty,
        output,
        delivered,
        toBackers,
        remainingUnits,
        atCm: po.ship_to_location_id === loc.id ? 0 : Math.max(0, q3(output - delivered - toBackers)),
        open,
        status: open ? "short" : OPEN_PO.has(po.status) ? "built" : "closed",
        buildable: 0,
        components: [],
        builtCost: builtUnitCost(mo, line, po.currency),
      });
    }
    const rank = (s: MakeOrderState) => (s.open ? (s.po.status === "draft" ? 1 : 0) : 2);
    states.sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.po.expected_ship ?? "9999").localeCompare(b.po.expected_ship ?? "9999") ||
        a.po.number.localeCompare(b.po.number, undefined, { numeric: true }) ||
        Number(a.line?.position ?? 0) - Number(b.line?.position ?? 0),
    );
    const poolAll = new Map<string, number>();
    const poolConfirmed = new Map<string, number>();
    for (const [id, n] of onHand) {
      poolAll.set(id, Math.max(0, n));
      poolConfirmed.set(id, Math.max(0, n - (transitQty.get(id) ?? 0)));
    }
    const reserved = new Map<string, number>();
    for (const s of states) {
      const used = consumed.get(s.mo.id) ?? new Map<string, number>();
      let ready = true;
      let arriving = true;
      let buildable = s.remainingUnits;
      for (const c of componentsOf(s.mo)) {
        const consigned = isConsigned(c);
        const remaining = s.open ? q3(c.per_unit * s.remainingUnits) : 0;
        let got = 0;
        let gotConfirmed = 0;
        if (consigned && remaining > 0) {
          got = Math.min(remaining, poolAll.get(c.item_id) ?? 0);
          poolAll.set(c.item_id, (poolAll.get(c.item_id) ?? 0) - got);
          gotConfirmed = Math.min(remaining, poolConfirmed.get(c.item_id) ?? 0);
          poolConfirmed.set(c.item_id, (poolConfirmed.get(c.item_id) ?? 0) - gotConfirmed);
          add(reserved, c.item_id, got);
          if (gotConfirmed + EPS < remaining) ready = false;
          if (got + EPS < remaining) arriving = false;
          buildable = Math.min(buildable, c.per_unit > 0 ? Math.floor(gotConfirmed / c.per_unit + EPS) : buildable);
        }
        s.components.push({
          ...c,
          consigned,
          need: q3(c.base_per_unit * s.qty),
          allowance: q3((c.per_unit - c.base_per_unit) * s.qty),
          total: q3(c.per_unit * s.qty),
          consumed: q3(used.get(c.item_id) ?? 0),
          remaining: consigned ? remaining : 0,
          reserved: q3(got),
          short: consigned ? Math.max(0, q3(remaining - got)) : 0,
          shortConfirmed: consigned ? Math.max(0, q3(remaining - gotConfirmed)) : 0,
        });
      }
      if (s.open) {
        s.status = ready ? "ready" : arriving ? "arriving" : "short";
        s.buildable = Math.max(0, buildable);
      }
    }

    // Stock lines: everything on hand or in transit there, and anything an open order still needs.
    const ids = new Set([...onHand.keys(), ...transitQty.keys()]);
    for (const s of states) if (s.open) for (const c of s.components) if (c.consigned) ids.add(c.item_id);
    // Pre-sales --- what backers' boxes still take here, after the builds.
    const claimed = new Map<string, number>();
    for (const c of d.claims ?? []) if (c.location_id === loc.id) add(claimed, c.item_id, Number(c.qty));
    const stock: CmStockLine[] = [];
    let stockValue = 0;
    let missing = 0;
    for (const id of ids) {
      const have = q3(onHand.get(id) ?? 0);
      const cost = costOf(id);
      const unitCost = cost == null ? null : mulRound(cost, 1, 4);
      const value = unitCost == null ? null : Math.max(0, have) * unitCost;
      if (have > EPS) {
        if (value == null) missing++;
        else stockValue += value;
      }
      const res = q3(reserved.get(id) ?? 0);
      const forBackers = q3(Math.min(claimed.get(id) ?? 0, Math.max(0, have - res)));
      stock.push({
        item: itemById.get(id),
        itemId: id,
        onHand: have,
        inTransit: q3(transitQty.get(id) ?? 0),
        reserved: res,
        forBackers,
        free: q3(have - res - forBackers),
        inShopify: inShopify.has(id),
        lastCountedAt: counted.get(id) ?? null,
        unitCost,
        value,
      });
    }
    stock.sort((a, b) => (a.item?.sku ?? "").localeCompare(b.item?.sku ?? "", undefined, { numeric: true, sensitivity: "base" }));

    // Finished goods waiting at the CM: local ones are in its ledger stock already; Shopify ones aren't.
    let finishedLocal = 0;
    let finishedShopify = 0;
    const lineOf = new Map(stock.map((l) => [l.itemId, l]));
    // Several orders can build the same item: each takes its share of what's on hand, never more than there is.
    const unclaimed = new Map(stock.map((l) => [l.itemId, Math.max(0, l.onHand)]));
    for (const s of states) {
      if (s.atCm <= EPS || !s.item) continue;
      if (s.item.source === "local") {
        const cost = lineOf.get(s.item.id)?.unitCost;
        const take = Math.min(s.atCm, unclaimed.get(s.item.id) ?? 0);
        unclaimed.set(s.item.id, (unclaimed.get(s.item.id) ?? 0) - take);
        if (cost != null) finishedLocal += take * cost;
      } else if (s.builtCost == null) missing++;
      else finishedShopify += s.atCm * s.builtCost;
    }

    const inbound = d.pos
      .filter((p) => p.ship_to_location_id === loc.id && p.kind !== "make" && ["sent", "acknowledged", "in_production", "shipped"].includes(p.status))
      .map((po) => {
        const lines = (linesByPo.get(po.id) ?? []).sort((a, b) => a.position - b.position);
        return { po, lines, due: lines.reduce((n, l) => n + Math.max(0, Number(l.qty) - Number(l.qty_received)), 0) };
      })
      .sort((a, b) => (a.po.expected_arrive ?? "9999").localeCompare(b.po.expected_arrive ?? "9999"));

    const counts = countsByCm.get(loc.id) ?? [];
    const lastCountedAt =
      counts
        .filter((c) => c.status === "approved")
        .map((c) => c.counted_at)
        .sort()
        .at(-1) ?? null;
    const finishedBuilds = states
      .filter((s) => s.output > 0 && s.remainingUnits <= EPS && lastOutput.has(s.mo.id))
      .map((s) => ({ label: `${s.po.number} ${s.item?.sku || s.item?.name || ""}`.trim(), at: lastOutput.get(s.mo.id) as string }));
    const due = countDue(
      loc.count_policy,
      lastCountedAt,
      finishedBuilds,
      stock.some((l) => l.onHand > EPS),
      now,
    );

    const money = (n: number) => mulRound(n, 1, 2);
    out.set(loc.id, {
      location: loc,
      stock,
      orders: states,
      inTransit,
      inbound,
      counts: { pending: counts.filter((c) => c.status === "submitted").sort((a, b) => a.counted_at.localeCompare(b.counted_at)), lastCountedAt, due },
      value: {
        stock: money(stockValue),
        inTransit: money(transitValue),
        parts: money(stockValue - finishedLocal),
        finished: money(finishedLocal + finishedShopify),
        total: money(stockValue + finishedShopify),
        missing,
      },
    });
  }
  return out;
}

// Count review ---------------------------------------------------------------------------------------------
// A count is what the CM had at counted_at, but the ledger only knows when things were recorded. Output reported
// after the count, a delivery received after it or a shipment confirmed after it may or may not be in the count, so
// approving asks about each one ("did this happen before they counted?") and posts only the variance still
// unexplained: counted − (book as of the count, less what was in transit then, + whatever the approver places
// before it). Settled without asking: HHL → CM shipments sent after the count (they weren't there yet), and earlier
// counts approved since (corrections as of before). Lines left alone: Shopify items (Shopify keeps their stock),
// finished goods of open make POs still waiting to be delivered (receiving moves them out of the CM), and parts a
// later count already settled.

export type CountDecision = "before" | "after";

/** Something recorded around the count that the approver must place before or after it. */
export type CountQuestion = {
  /** "consign_shipment:<id>", "cm_report:<id>", "receipt:<id>" or "move:<id>". */
  key: string;
  label: string;
  /** When it was recorded (a shipment: when it was sent). */
  at: string;
  /** What it does to the counted parts at the CM if it happened before the count. */
  effects: { item_id: string; qty: number }[];
};

export type CountLineResult = {
  item_id: string;
  counted: number;
  /** What the count is held against: the book as of the count, plus what the approver placed before it. */
  book: number;
  variance: number;
  outcome: "adjusted" | "superseded" | "awaiting_delivery" | "shopify";
};

export type CountReviewData = {
  count: Pick<CmCount, "id" | "location_id" | "counted_at">;
  lines: readonly Pick<CmCountLine, "item_id" | "counted" | "expected" | "in_shopify">[];
  /** Ledger moves (any; only the count's location and parts are read). */
  moves: readonly Pick<StockMove, "id" | "item_id" | "from_location_id" | "to_location_id" | "qty" | "ref_type" | "ref_id" | "at" | "notes">[];
  shipments: readonly Pick<ConsignShipment, "id" | "number" | "direction" | "cm_location_id" | "shipped_at" | "received_at">[];
  shipmentLines: readonly Pick<ConsignShipmentLine, "shipment_id" | "item_id" | "qty">[];
  counts: readonly Pick<CmCount, "id" | "number" | "counted_at">[];
  makeOrders: readonly Pick<MakeOrder, "id" | "po_id" | "po_line_id" | "item_id" | "cm_location_id" | "qty_output">[];
  pos: readonly Pick<Po, "id" | "number" | "status" | "ship_to_location_id">[];
  poLines: readonly (Pick<PoLine, "id" | "qty_received" | "qty_rejected"> & Partial<Pick<PoLine, "qty_direct_shipped">>)[];
  reports: readonly Pick<CmReport, "id" | "kind" | "make_order_id" | "qty_output" | "auto">[];
  receipts: readonly Pick<Receipt, "id" | "number">[];
  items: readonly Pick<Item, "id" | "sku" | "name">[];
};

const DELIVERING = new Set(["sent", "acknowledged", "in_production", "shipped"]);

/** Finished goods of open make POs at a CM, built and still waiting to be delivered elsewhere. */
export function awaitingDelivery(
  locationId: string,
  makeOrders: CountReviewData["makeOrders"],
  pos: CountReviewData["pos"],
  poLines: CountReviewData["poLines"],
): Set<string> {
  const poById = new Map(pos.map((p) => [p.id, p]));
  const lineById = new Map(poLines.map((l) => [l.id, l]));
  const out = new Set<string>();
  for (const m of makeOrders) {
    const po = poById.get(m.po_id);
    const line = lineById.get(m.po_line_id);
    if (m.cm_location_id !== locationId || !po || !line || po.ship_to_location_id === locationId || !DELIVERING.has(po.status)) continue;
    // Pre-sales --- units sent straight to backers left the CM too.
    if (Number(m.qty_output) > Number(line.qty_received) + Number(line.qty_rejected) + Number(line.qty_direct_shipped ?? 0) + EPS) out.add(m.item_id);
  }
  return out;
}

export function reviewCount(
  d: CountReviewData,
  decisions: Readonly<Record<string, CountDecision>> = {},
): { questions: CountQuestion[]; undecided: CountQuestion[]; lines: CountLineResult[] } {
  const loc = d.count.location_id;
  const T = Date.parse(d.count.counted_at);
  const after = (iso: string | null | undefined) => iso != null && Date.parse(iso) > T;
  const shipmentById = new Map(d.shipments.map((s) => [s.id, s]));
  const countById = new Map(d.counts.map((c) => [c.id, c]));
  const reportById = new Map(d.reports.map((r) => [r.id, r]));
  const moById = new Map(d.makeOrders.map((m) => [m.id, m]));
  const poById = new Map(d.pos.map((p) => [p.id, p]));
  const receiptById = new Map(d.receipts.map((r) => [r.id, r]));
  const sku = new Map(d.items.map((i) => [i.id, i.sku || i.name]));
  const here = d.moves.filter((m) => m.from_location_id === loc || m.to_location_id === loc);
  const effect = (m: (typeof here)[number]) => (m.to_location_id === loc ? Number(m.qty) : -Number(m.qty));

  // Parts a count taken later has already settled, and corrections from counts taken earlier (they come first).
  const superseded = new Set<string>();
  const autoBefore = new Map<string, number>();
  for (const m of here) {
    if (m.ref_type !== "cm_count" || !after(m.at) || m.ref_id === d.count.id) continue;
    const other = countById.get(m.ref_id ?? "");
    if (other && after(other.counted_at)) superseded.add(m.item_id);
    else autoBefore.set(m.item_id, (autoBefore.get(m.item_id) ?? 0) + effect(m));
  }
  const waiting = awaitingDelivery(loc, d.makeOrders, d.pos, d.poLines);
  const outcomeOf = (l: CountReviewData["lines"][number]): CountLineResult["outcome"] =>
    l.in_shopify ? "shopify" : waiting.has(l.item_id) ? "awaiting_delivery" : superseded.has(l.item_id) ? "superseded" : "adjusted";
  const adjustable = new Set(d.lines.filter((l) => outcomeOf(l) === "adjusted").map((l) => l.item_id));

  const byKey = new Map<string, CountQuestion>();
  const ask = (key: string, label: string, at: string, itemId: string, qty: number) => {
    let q = byKey.get(key);
    if (!q) {
      q = { key, label, at, effects: [] };
      byKey.set(key, q);
    }
    const e = q.effects.find((x) => x.item_id === itemId);
    if (e) e.qty = q3(e.qty + qty);
    else q.effects.push({ item_id: itemId, qty: q3(qty) });
  };
  const reportLabel = (reportId: string) => {
    const r = reportById.get(reportId);
    const mo = moById.get(r?.make_order_id ?? "");
    const po = poById.get(mo?.po_id ?? "");
    if (!r) return "A report from the CM";
    if (r.kind === "scrap") return `Scrap reported${po ? ` on ${po.number}` : ""}`;
    const built = `${po?.number ?? "A build"} ${sku.get(mo?.item_id ?? "") ?? ""}`.trim();
    return `${built}: ${Number(r.qty_output)} built${r.auto ? " (reported by a receipt)" : ""}`;
  };

  // HHL → CM shipments on their way at the count: were they there when the CM counted?
  const linesOf = new Map<string, { item_id: string; qty: number }[]>();
  for (const l of d.shipmentLines) linesOf.set(l.shipment_id, [...(linesOf.get(l.shipment_id) ?? []), { item_id: l.item_id, qty: Number(l.qty) }]);
  const transitLabel = (s: CountReviewData["shipments"][number]) =>
    `${s.number}: sent before the count, ${s.received_at ? "confirmed after it" : "not confirmed yet"}`;
  for (const s of d.shipments) {
    if (s.cm_location_id !== loc || s.direction !== "to_cm" || after(s.shipped_at) || (s.received_at && !after(s.received_at))) continue;
    for (const l of linesOf.get(s.id) ?? []) if (adjustable.has(l.item_id)) ask(`consign_shipment:${s.id}`, transitLabel(s), s.shipped_at, l.item_id, l.qty);
  }
  // Everything else recorded at the CM after the count.
  for (const m of here) {
    if (!after(m.at) || !adjustable.has(m.item_id) || m.ref_type === "cm_count") continue;
    if (m.ref_type === "consign_shipment") {
      const s = shipmentById.get(m.ref_id ?? "");
      if (s?.direction === "to_cm" && after(s.shipped_at)) continue; // sent after the count: it can't have been there
      if (s?.direction === "to_cm") ask(`consign_shipment:${s.id}`, transitLabel(s), s.shipped_at, m.item_id, effect(m));
      else ask(`consign_shipment:${m.ref_id}`, `${s?.number ?? "A return"}: back to HHL, recorded after the count`, m.at, m.item_id, effect(m));
    } else if (m.ref_type === "cm_report") {
      ask(`cm_report:${m.ref_id}`, `${reportLabel(m.ref_id ?? "")}, recorded after the count`, m.at, m.item_id, effect(m));
    } else if (m.ref_type === "receipt") {
      const what = effect(m) > 0 ? "received at the CM" : "delivered from the CM";
      ask(`receipt:${m.ref_id}`, `${receiptById.get(m.ref_id ?? "")?.number ?? "A receipt"}: ${what}, recorded after the count`, m.at, m.item_id, effect(m));
    } else if (m.ref_type === "presale_shipment") {
      // Pre-sales --- boxes sent to backers from the CM, or a shipment taken back (its units back in the CM's book).
      if (effect(m) > 0)
        ask(`presale_shipment:${m.ref_id}:back`, "Back from backers (shipment taken back), recorded after the count", m.at, m.item_id, effect(m));
      else ask(`presale_shipment:${m.ref_id}`, `Sent to backers${m.notes ? ` (${m.notes})` : ""}, recorded after the count`, m.at, m.item_id, effect(m));
    } else ask(`move:${m.id}`, `An adjustment recorded after the count${m.notes ? ` (${m.notes})` : ""}`, m.at, m.item_id, effect(m));
  }
  const questions = [...byKey.values()].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const undecided = questions.filter((q) => decisions[q.key] !== "before" && decisions[q.key] !== "after");

  const lines = d.lines.map((l) => {
    let book = Number(l.expected) + (autoBefore.get(l.item_id) ?? 0);
    for (const q of questions) if (decisions[q.key] === "before") for (const e of q.effects) if (e.item_id === l.item_id) book += e.qty;
    book = q3(book);
    return { item_id: l.item_id, counted: Number(l.counted), book, variance: q3(Number(l.counted) - book), outcome: outcomeOf(l) };
  });
  return { questions, undecided, lines };
}

/**
 * A count difference big enough to count again (Mitch, 2026-11-03: counts post when entered; big differences aren't
 * blocked, they're flagged for a recount): over COUNT_AGAIN_PCT of what the books said and over COUNT_AGAIN_USD in value.
 */
export const COUNT_AGAIN_PCT = 10;
export const COUNT_AGAIN_USD = 100;
export function countAgain(l: { book: number; variance: number; unitCost: number | null }): boolean {
  if (!l.variance || l.unitCost == null) return false;
  const pct = (Math.abs(l.variance) / Math.max(Math.abs(l.book), 1)) * 100;
  return pct > COUNT_AGAIN_PCT && Math.abs(l.variance * l.unitCost) > COUNT_AGAIN_USD;
}
